import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { refreshToken, redirectToLoginPage, getEstimatedExpiry, setEstimatedExpiry } from '@/lib/auth';
import type { User } from '@/types/api';

interface AuthContextValue {
  user: User;
  refetch: () => void;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

const REFRESH_BUFFER_MS = 2 * 60 * 1000;   // 2 min vor Ablauf
const CHECK_INTERVAL_MS = 30 * 1000;       // alle 30s prüfen
const SESSION_CACHE_MS = 60 * 1000;        // 60s (analog auth.php)

export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiGet<User>('/user/me'),
    staleTime: SESSION_CACHE_MS,
    retry: 1,
  });

  const isRefreshingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (cancelled || isRefreshingRef.current) return;
      const expiry = getEstimatedExpiry();
      const needs = expiry === 0 || expiry - Date.now() < REFRESH_BUFFER_MS;
      if (!needs) return;
      isRefreshingRef.current = true;
      try {
        const ok = await refreshToken();
        if (!ok) {
          redirectToLoginPage('session_expired');
        }
      } finally {
        isRefreshingRef.current = false;
      }
    }

    void check();
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) void check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (query.data?.user_id && getEstimatedExpiry() === 0) {
      setEstimatedExpiry(900); // 15 min Default
    }
  }, [query.data?.user_id]);

  if (query.isLoading) {
    return (
      <div className="auth-loading" role="status" aria-live="polite">
        <div className="spinner" />
        <p>KISpro wird geladen …</p>
      </div>
    );
  }

  if (query.isError || !query.data) {
    redirectToLoginPage();
    return null;
  }

  if (!query.data.htcid) {
    window.location.href = `${import.meta.env.VITE_LOGIN_BASE_URL}/apps`;
    return null;
  }

  if (query.data.show_neo_instance === false) {
    window.location.href = `${import.meta.env.VITE_LOGIN_BASE_URL}/apps`;
    return null;
  }

  return (
    <AuthCtx.Provider value={{ user: query.data, refetch: () => void query.refetch() }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
